
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Message, Conversation } from "@/types";
import { updateConversationTitleService } from "../services";
import { useRef } from 'react';
import {
  generateSummaryForDocument,
  generateAIResponse
} from "../messagingService";
// 🎯 CHAT GERAL EXCLUSIVO: usar RPC e processador isolados
import { saveGeneralMessageRPC } from "../services/generalChatMessageService";
import { processGeneralFileAndCreateMessage } from "../generalFileProcessor";
import { analyzeMessageComplexity, getQuickResponse } from "../optimizations/complexityDetector";
import { detectPromptTemplate, applyPromptTemplate } from "../optimizations/promptTemplateManager";
import { getCachedSemanticContext, formatRelevantMessagesForPrompt } from "../optimizations/semanticMemory";
import { fetchStreamingResponse } from '@/services/streamingApiService';

export function useMessageActions(
  messages: Message[],
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
  currentConversation: Conversation | null,
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  fetchConversations: (userId: string) => Promise<void>,
  fetchMessages: (conversationId: string) => Promise<void>,
  createNewConversation: (userId: string) => Promise<string | null>,
  conversations: Conversation[]
) {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // 🔒 STREAMING: Um reader por vez
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const sendMessage = async (
    content: string, 
    file: File | null = null,
    userId: string,
    onFileProgress?: (progress: number) => void
  ) => {
    try {
      if (!userId) return;
      
      setIsProcessing(true);
      
      let conversationId = currentConversation?.id;
      
      // Verificar se a conversa atual é válida antes de prosseguir
      if (conversationId && !conversations.some(c => c.id === conversationId)) {
        console.warn("⚠️ Conversa atual não encontrada na lista, redirecionando...");
        navigate('/chat', { replace: true });
        toast({
          title: "Conversa não encontrada",
          description: "Redirecionando para o chat principal...",
          variant: "default"
        });
        setIsProcessing(false);
        return;
      }
      
      if (!conversationId) {
        console.log("🆕 Nenhuma conversa atual, criando nova automaticamente para usuário:", userId);
        
        // Tentar criar nova conversa com retry
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries && !conversationId) {
          try {
            console.log(`🔄 Tentativa ${retryCount + 1} de criar nova conversa...`);
            conversationId = await createNewConversation(userId);
            
            if (conversationId) {
              console.log("✅ Nova conversa criada com sucesso:", conversationId);
              navigate(`/chat/${conversationId}`);
              await new Promise(resolve => setTimeout(resolve, 100)); // Wait for navigation to complete
              break;
            } else {
              console.warn(`⚠️ Tentativa ${retryCount + 1} falhou - conversationId é null`);
            }
          } catch (error) {
            console.error(`❌ Erro na tentativa ${retryCount + 1} de criar conversa:`, error);
          }
          
          retryCount++;
          if (retryCount < maxRetries) {
            console.log(`⚡ Tentativa imediata ${retryCount + 1}...`);
          }
        }
        
        if (!conversationId) {
          console.error("💥 Falha ao criar nova conversa após todas as tentativas");
          toast({
            title: "Erro",
            description: "Não foi possível criar uma nova conversa após várias tentativas. Por favor, recarregue a página e tente novamente.",
            variant: "destructive",
          });
          return;
        }

        // Forçar atualização de conversas
        await fetchConversations(userId);
      }
      
      // 🎯 CHAT GERAL: usar processador exclusivo com RPC
      console.log("[CHAT GERAL] Processando arquivo e criando mensagem, file:", file?.name);
      
      // 🔒 IDEMPOTÊNCIA: Gerar request_id único para rastreamento
      const request_id = crypto.randomUUID();
      console.log(`🔑 [SEND] Request ID gerado: ${request_id}`);
      
      // ✨ UI OTIMISTA: Criar mensagem pendente com client_msg_id
      const client_msg_id = crypto.randomUUID();
      const client_ts = Date.now();
      
      const { userMessage, fileObject, extractedText, documentAnalysis } = await processGeneralFileAndCreateMessage(
        file, 
        content, 
        conversationId, 
        userId,
        onFileProgress
      );
      
      // Adicionar metadata de reconciliação + request_id
      userMessage.metadata = {
        ...userMessage.metadata,
        client_msg_id,
        client_ts,
        request_id,
        status: 'final'
      };
      
      // 🔑 CHAVE ÚNICA: origin_user_message_id para relação 1:1 pergunta→resposta
      const originUserMessageId = userMessage.id;
      console.log(`🔗 [SEND] Origin User Message ID: ${originUserMessageId}`);
      
      // Update messages state with user message
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      
      // Set conversation title from first message if it's a new conversation
      if (messages.length === 0) {
        const conversationTitle = file 
          ? `Análise de ${file.name}`
          : content.length > 40 ? content.substring(0, 40) + "..." : content;
          
        const titleUpdated = await updateConversationTitleService(
          conversationId, 
          conversationTitle, 
          userId
        );
      }
      
      // Check file type to determine processing strategy
      const isImageFile = file && file.type.startsWith('image/');
      const isPdfFile = file && file.type === 'application/pdf';
      const isLongDocument = extractedText && extractedText.length > 5000;
      
      console.log("📋 File processing strategy:", {
        isImageFile,
        isPdfFile,
        isLongDocument,
        extractedTextLength: extractedText?.length || 0,
        hasDocumentAnalysis: !!documentAnalysis
      });
      
      // For document analysis (structured documents), generate contextual summary with suggestions
      if (file && documentAnalysis && extractedText && extractedText.length > 0) {
        console.log("📄 Document with analysis detected, generating contextual summary...");
        
        try {
          const summaryMessage = await generateSummaryForDocument(
            extractedText, 
            conversationId, 
            updatedMessages,
            userId,
            request_id,
            originUserMessageId
          );
          
          if (summaryMessage) {
            // 🎯 CHAT GERAL: usar RPC exclusiva (SALVAMENTO ÚNICO)
            console.log("💾 [CHAT GERAL] Salvando resumo via RPC exclusiva...");
            await saveGeneralMessageRPC({
              conversationId: summaryMessage.conversation_id,
              role: summaryMessage.role,
              content: summaryMessage.content,
              fileId: summaryMessage.file?.id || null,
              requestId: request_id,
              originUserMessageId: originUserMessageId
            });
            console.log("✅ [CHAT GERAL] Resumo salvo via RPC exclusiva (ÚNICO)");
            
            // Adicionar resumo ao estado da UI
            setMessages([...updatedMessages, summaryMessage]);
            
            // ⚖️ CORREÇÃO: Não re-buscar mensagens (UI otimista já tem)
            setIsProcessing(false);
            void fetchConversations(userId);
            return;
          }
        } catch (summaryError) {
          console.error("Error generating contextual summary:", summaryError);
          // Continue to fallback if summary generation fails
        }
      }
      
      // For long PDF documents (over 5000 chars), generate summary
      else if (isPdfFile && isLongDocument) {
        console.log("📊 Long PDF document detected, generating summary...");
        
        try {
          const summaryMessage = await generateSummaryForDocument(
            extractedText, 
            conversationId, 
            updatedMessages,
            userId,
            request_id,
            originUserMessageId
          );
          
          if (summaryMessage) {
            // 🎯 CHAT GERAL: usar RPC exclusiva (SALVAMENTO ÚNICO)
            console.log("💾 [CHAT GERAL] Salvando resumo longo via RPC exclusiva...");
            await saveGeneralMessageRPC({
              conversationId: summaryMessage.conversation_id,
              role: summaryMessage.role,
              content: summaryMessage.content,
              fileId: summaryMessage.file?.id || null,
              requestId: request_id,
              originUserMessageId: originUserMessageId
            });
            console.log("✅ [CHAT GERAL] Resumo longo salvo via RPC exclusiva (ÚNICO)");
            
            // Adicionar resumo longo ao estado da UI
            setMessages([...updatedMessages, summaryMessage]);
            
            // ⚖️ CORREÇÃO: Não re-buscar mensagens (UI otimista já tem)
            setIsProcessing(false);
            void fetchConversations(userId);
            return;
          }
        } catch (summaryError) {
          console.error("Error generating document summary:", summaryError);
          // Continue to normal AI response if summary fails
        }
      }
      
      
      // ✨ OTIMIZAÇÃO PRIORITÁRIA: Verificar resposta rápida PRIMEIRO (antes de qualquer processamento)
      console.log("⚡ Verificando cache de respostas rápidas...");
      const quickResponse = getQuickResponse(content);
      if (quickResponse && !file) {
        console.log("⚡ RESPOSTA RÁPIDA ENCONTRADA! Retornando imediatamente do cache");
        
        // ✨ UI OTIMISTA: Criar placeholder da IA imediatamente
        const assistantClientId = crypto.randomUUID();
        const quickMessage: Message = {
          id: uuidv4(),
          conversation_id: conversationId,
          role: "assistant",
          content: quickResponse,
          created_at: new Date().toISOString(),
          metadata: { 
            api_used: 'cache', 
            processing_time: '<50ms',
            response_type: 'quick_cache',
            client_msg_id: assistantClientId,
            client_ts: Date.now(),
            status: 'final'
          }
        };
        
        console.log("💾 [CHAT GERAL] Salvando resposta rápida via RPC exclusiva (ÚNICO)...");
        await saveGeneralMessageRPC({
          conversationId: quickMessage.conversation_id,
          role: quickMessage.role,
          content: quickMessage.content,
          fileId: null,
          requestId: request_id,
          originUserMessageId: originUserMessageId
        });
        
        // Adicionar resposta rápida ao estado da UI
        setMessages([...updatedMessages, quickMessage]);
        
        // ⚖️ CORREÇÃO: Não re-buscar mensagens (UI otimista já tem)
        void fetchConversations(userId);
        console.log("✅ Resposta rápida processada e sincronizada - ZERO API calls");
        return; // RETORNA IMEDIATAMENTE - não continua processamento
      }

      // ✨ OTIMIZAÇÃO: Análise de complexidade inteligente
      const hasDocumentInConversation = extractedText && extractedText.length > 0;
      const complexityAnalysis = analyzeMessageComplexity(content, messages, hasDocumentInConversation);
      
      console.log(`🧠 Análise de complexidade:`, {
        level: complexityAnalysis.level,
        score: complexityAnalysis.score,
        shouldUseCoT: complexityAnalysis.shouldUseCoT,
        estimatedTime: complexityAnalysis.estimatedProcessingTime,
        reasons: complexityAnalysis.reasons
      });

      // ✨ OTIMIZAÇÃO: Seleção de template dinâmico (usar template da conversa ou detectar)
      const conversationTemplate = currentConversation?.metadata?.template;
      const templateName = conversationTemplate || detectPromptTemplate(content, hasDocumentInConversation);
      console.log(`🎯 Template selecionado: ${templateName} ${conversationTemplate ? '(conversa)' : '(auto-detectado)'}`);

      // ✨ OTIMIZAÇÃO: Contexto semântico inteligente
      const semanticContext = getCachedSemanticContext(content, messages, 8);
      const memoryContextPrompt = formatRelevantMessagesForPrompt(semanticContext);
      
      console.log(`🧠 Contexto semântico:`, {
        totalMessages: semanticContext.totalMessages,
        selectedMessages: semanticContext.selectedMessages,
        summary: semanticContext.contextSummary
      });

      // For all other cases (including images), generate questions and analysis
      console.log("🤖 Generating optimized AI response...");
      
      // Prepare message for AI processing
      let messageInput = content;
      
      if (file && extractedText) {
        if (isImageFile) {
          messageInput = `${content}\n\n[IMAGEM ANALISADA: ${file.name}]\nTexto extraído da imagem:\n${extractedText}\n\nPor favor, analise este conteúdo e gere 5 perguntas inteligentes sobre o que posso fazer com essas informações.`;
        } else {
          messageInput = `${content}\n\n[DOCUMENTO: ${file.name}]\nConteúdo:\n${extractedText}\n\nAnalise este documento e sugira perguntas úteis sobre como posso usar essas informações.`;
        }
      }
        
      // ✨ STREAMING INTELIGENTE: usar streaming para respostas complexas/longas
      const useStreaming = complexityAnalysis.level === 'complex' || 
                           complexityAnalysis.score > 6 || 
                           hasDocumentInConversation ||
                           content.length > 200;
      
      console.log(`🔀 Decisão de resposta: ${useStreaming ? 'STREAMING' : 'NORMAL'} (complexidade: ${complexityAnalysis.level})`);
      
      let usedStreaming = false;
      
      // Tentar streaming real-time se aplicável
      if (useStreaming) {
        console.log("🚀 Iniciando streaming real-time...");
        
        try {
          // 🔒 Cancelar stream anterior se existir
          if (readerRef.current) {
            console.log('⚠️ Stream anterior ativo, cancelando...');
            await readerRef.current.cancel();
            readerRef.current = null;
          }
          
          if (controllerRef.current) {
            controllerRef.current.abort();
          }
          
          // Iniciar stream real
          const controller = new AbortController();
          controllerRef.current = controller;
          let accumulated = '';
          
          const streamPayload = {
            messages: updatedMessages.map(m => ({ 
              role: m.role as 'system' | 'user' | 'assistant', 
              content: m.content 
            })),
            conversation_id: conversationId,
            model: complexityAnalysis.level === 'complex' ? 'gpt-4.1-2025-04-14' : 'gpt-5-2025-08-07',
            stream: true,
            use_cot: complexityAnalysis.shouldUseCoT,
            document_text: extractedText || '',
            template: templateName,
            request_id: request_id
          };
          
          const streamResponse = await fetchStreamingResponse({
            payload: streamPayload,
            signal: controller.signal
          });

          // ⚖️ CORREÇÃO CIRÚRGICA: Só criar placeholder quando stream conectar
          const assistantClientId = crypto.randomUUID();
          const placeholderMessage: Message = {
            id: uuidv4(),
            conversation_id: conversationId,
            role: 'assistant',
            content: '',
            created_at: new Date().toISOString(),
            metadata: { 
              isStreaming: true,
              template: templateName === 'abnt_juridico' ? 'abnt_juridico' : undefined,
              client_msg_id: assistantClientId,
              client_ts: Date.now(),
              request_id: request_id,
              status: 'pending'
            }
          };
          
          // Adicionar placeholder após stream conectar
          const messagesWithPlaceholder = [...updatedMessages, placeholderMessage];
          setMessages(messagesWithPlaceholder);
          
          // Consumir stream token por token
          for await (const { content: token, isComplete } of streamResponse) {
            if (controller.signal.aborted) break;
            
            if (token) {
              accumulated += token;
              // Atualizar mensagem em tempo real
              setMessages(prev => {
                const copy = [...prev];
                const lastIndex = copy.length - 1;
                if (copy[lastIndex]?.id === placeholderMessage.id) {
                  copy[lastIndex] = {
                    ...copy[lastIndex],
                    content: accumulated
                  };
                }
                return copy;
              });
            }
            
            if (isComplete) {
              console.log("✅ Streaming concluído");
              break;
            }
          }
          
          // 🔒 Limpar refs após streaming
          readerRef.current = null;
          controllerRef.current = null;
          
          // Finalizar: remover flag isStreaming e salvar
          const finalMessage = {
            id: placeholderMessage.id,
            conversation_id: conversationId,
            role: 'assistant' as const,
            content: accumulated,
            metadata: { 
              template: templateName === 'abnt_juridico' ? 'abnt_juridico' : undefined,
              isStreaming: false,
              client_msg_id: assistantClientId,
              client_ts: Date.now(),
              request_id: request_id,
              status: 'final'
            }
          };
          
          setMessages(prev => {
            const copy = [...prev];
            const lastIndex = copy.length - 1;
            if (copy[lastIndex]?.id === placeholderMessage.id) {
              copy[lastIndex] = {
                ...copy[lastIndex],
                content: accumulated,
                metadata: finalMessage.metadata
              };
            }
            return copy;
          });
          
          // 🎯 CHAT GERAL: Salvar mensagem final via RPC exclusiva (SALVAMENTO ÚNICO)
          if (accumulated.trim()) {
            console.log("💾 [CHAT GERAL] Salvando streaming via RPC exclusiva (ÚNICO)...");
            await saveGeneralMessageRPC({
              conversationId: finalMessage.conversation_id,
              role: finalMessage.role,
              content: finalMessage.content,
              fileId: null,
              requestId: request_id,
              originUserMessageId: originUserMessageId
            });
            console.log("✅ [CHAT GERAL] Streaming salvo via RPC exclusiva (ÚNICO) - FINALIZANDO");
            
            // ⚖️ CORREÇÃO: Não re-buscar mensagens (UI otimista já tem)
            void fetchConversations(userId);
            
            // ✅ RETURN ABSOLUTO - bloquear fallback
            return;
          }
          
        } catch (streamError) {
          console.warn("⚠️ Streaming falhou, usando fallback normal:", streamError);
          // 🔒 Limpar refs em caso de erro
          readerRef.current = null;
          controllerRef.current = null;
          // Não remover mensagens, deixar fallback tratar
        }
      }
      
      // ✅ VERIFICAÇÃO DUPLA: só executar se streaming realmente falhou
      console.log("🔍 Verificando necessidade de fallback...");
      if (!usedStreaming) {
        console.log("🤖 Gerando resposta IA modo normal...");
        
        // ✨ OTIMIZAÇÃO: Generate AI response com template especializado
        const assistantMessage = await generateAIResponse(
          updatedMessages,
          messageInput,
          conversationId,
          userId,
          {
            templateName,
            complexityAnalysis,
            semanticContext,
            memoryContextPrompt,
            requestId: request_id,
            originUserMessageId: originUserMessageId
          }
        );
        
        if (assistantMessage) {
          // ✅ Mensagem já salva pelo generateAIResponse via RPC exclusiva
          console.log("✅ [CHAT GERAL] Resposta IA gerada e salva via RPC exclusiva");
          
          // Adicionar resposta da IA ao estado da UI
          setMessages([...updatedMessages, assistantMessage]);
          
          // ⚖️ CORREÇÃO: Não re-buscar mensagens (UI otimista já tem)
          void fetchConversations(userId);
        }
      }
      
      await fetchConversations(userId);
    } catch (error) {
      console.error("Error sending message:", error);
      
      // 🔒 Limpar refs em caso de erro
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      
      toast({
        title: "Erro",
        description: "Falha ao processar sua mensagem. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return { sendMessage };
}
